import axios from 'axios';

const BASE_URL = process.env.VUE_APP_API_BASEURL + 'mdm/command-logs';

/** Set the JWT auth header for every request */
function setAuthHeader() {
    axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
}

/**
 * Fetch paginated/filtered command logs.
 * @param {{ type: number, status?: number|string, search?: string, paginate?: boolean }} params
 */
export async function getCommandLogs(params) {
    setAuthHeader();
    const response = await axios.get(BASE_URL, { params });
    return response.data;
}

/**
 * Bulk update status for one or more logs.
 * @param {number[]} ids
 * @param {'request'|'approve'|'reject'|'move_to_lock'|'request_unlock'} action
 * @returns Full axios response (to allow 207 detection by caller)
 */
export async function updateCommandLogsStatus(ids, action) {
    setAuthHeader();
    // Use validateStatus so axios doesn't throw on 207
    const response = await axios.post(
        `${BASE_URL}/status`,
        { ids, action },
        { validateStatus: (status) => status >= 200 && status < 300 }
    );
    return response;
}

/**
 * Import devices from an Excel / CSV file.
 * @param {File} file
 * @param {number} type  Process type: 1=Send Warning, 2=Enable Lost Mode, 3=Disable Lost Mode
 */
export async function importCommandLogs(file, type) {
    setAuthHeader();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await axios.post(`${BASE_URL}/import/excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
}

/**
 * Download the sample Excel template as a blob.
 * Caller is responsible for triggering the browser download.
 */
export async function downloadSampleExcel() {
    setAuthHeader();
    const response = await axios.get(`${BASE_URL}/sample-excel`, {
        responseType: 'blob',
    });
    return response;
}

/**
 * Pre-check MDM import file (Excel).
 * @param {File} file
 * @param {number} type  Process type: 1=Send Warning, 2=Enable Lost Mode, 3=Disable Lost Mode
 */
export async function preCheckImport(file, type) {
    setAuthHeader();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await axios.post(`${BASE_URL}/import/excel/pre-check`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
}

/**
 * Submit MDM import file to queue after pre-check (Excel).
 * @param {File} file
 * @param {number} type  Process type: 1=Send Warning, 2=Enable Lost Mode, 3=Disable Lost Mode
 * @param {number|null} templateId ID of the message template to use
 */
export async function submitImport(file, type, templateId = null) {
    setAuthHeader();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    if (templateId) {
        formData.append('template_id', templateId);
    }

    const response = await axios.post(`${BASE_URL}/import/excel/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
}

/**
 * Pre-check a single contract for manual entry.
 * @param {string} contractNumber 
 * @param {number} type 
 */
export async function preCheckBulk(contractNumber, type) {
    setAuthHeader();
    const response = await axios.get(`${BASE_URL}/import/bulk/pre-check`, {
        params: { contract_number: contractNumber, type }
    });
    return response.data;
}

/**
 * Submit a list of contracts for manual entry.
 * @param {string[]} contracts 
 * @param {number} type 
 * @param {number} templateId 
 */
export async function submitBulk(contracts, type, templateId) {
    setAuthHeader();
    const response = await axios.post(`${BASE_URL}/import/bulk/submit`, {
        contracts,
        type,
        template_id: templateId
    });
    return response.data;
}

/**
 * Approve an entire import batch.
 * @param {number} id Batch ID
 */
export async function approveImportBatch(id) {
    setAuthHeader();
    const url = `${process.env.VUE_APP_API_BASEURL}mdm/import-batches/${id}/approve`;
    const response = await axios.post(url);
    return response.data;
}

/**
 * Reject an entire import batch.
 * @param {number} id Batch ID
 */
export async function rejectImportBatch(id) {
    setAuthHeader();
    const url = `${process.env.VUE_APP_API_BASEURL}mdm/import-batches/${id}/reject`;
    const response = await axios.post(url);
    return response.data;
}

/**
 * Fetch list of MDM import batches.
 * @param {object} params Pagination and filtering params.
 */
export async function getImportBatches(params) {
    setAuthHeader();
    const url = process.env.VUE_APP_API_BASEURL + 'mdm/import-batches';
    const response = await axios.get(url, { params });
    return response.data;
}

/**
 * Delete a single command log (only DRAFT status allowed by BE).
 * @param {number} id
 */
export async function deleteCommandLog(id) {
    setAuthHeader();
    const response = await axios.delete(`${BASE_URL}/${id}`);
    return response.data;
}
